import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_FINISH, FREE_BOARD_FINISHES } from '~/store/boardFinishes.js';
import { DEFAULT_CARD_BACK, FREE_CARD_BACKS } from '~/store/cardBacks.js';
import { DEFAULT_CARD_FRONT, FREE_CARD_FRONTS } from '~/store/cardFronts.js';
import { useEntitlement } from '~/store/membership.js';
import {
  BOT_SPEEDS,
  type BotSpeed,
  CROSS_POSITIONS,
  type CrossPosition,
  DECK_STYLES,
  type DeckStyle,
  type SettingsState,
  useSettingsStore,
} from '~/store/settingsStore.js';
import { BoardFinishPicker } from '~/ui/screens/settings/BoardFinishPicker.js';
import { CardBackPicker } from '~/ui/CardBackPicker.js';
import { CardFrontPicker } from '~/ui/CardFrontPicker.js';
import { Choice } from '~/ui/Choice.js';
import { Level } from '~/ui/Level.js';
import { Switch } from '~/ui/Switch.js';
import {
  MemberGate,
  type OfferDoor,
  type OfferReturn,
  PaywallTag,
  parkedPreview,
} from './MemberGate.js';

/**
 * Every device preference, one row each.
 *
 * Two surfaces draw them: the Settings screen, in its groups
 * (`PreferencePages.tsx`), and the table's drawer, as one list
 * (`ui/SettingsList.tsx`). Both write the same store, so the values could not
 * disagree; the labels, hints and option lists could, and a preference added
 * to one surface would be missing from the other. So each row has one owner,
 * here, and the two surfaces only choose the grouping.
 */

export function DeckChoice(): React.JSX.Element {
  const { t } = useTranslation();
  const deck = useSettingsStore((state) => state.deck);
  return (
    <Choice
      label={t('settings.deck')}
      value={deck}
      options={DECK_STYLES.map((value) => ({
        value,
        label: t(value === 'classic' ? 'settings.classic' : 'settings.fourColour'),
      }))}
      onChange={(value) => {
        useSettingsStore.getState().set('deck', value as DeckStyle);
      }}
    />
  );
}

export function CardFrontRow({ offer }: { readonly offer?: OfferReturn }): React.JSX.Element {
  const deck = useSettingsStore((state) => state.deck);
  const front = useGatedStyle('cardFront', FREE_CARD_FRONTS, DEFAULT_CARD_FRONT, 'front', offer);
  return (
    <>
      <CardFrontPicker
        value={front.value}
        fourColor={deck === 'four-colour'}
        onChange={front.onChange}
        tag={front.tag}
      />
      {front.gate}
    </>
  );
}

export function CardBackRow({ offer }: { readonly offer?: OfferReturn }): React.JSX.Element {
  const back = useGatedStyle('cardBack', FREE_CARD_BACKS, DEFAULT_CARD_BACK, 'back', offer);
  return (
    <>
      <CardBackPicker value={back.value} onChange={back.onChange} tag={back.tag} />
      {back.gate}
    </>
  );
}

export function BoardFinishRow({ offer }: { readonly offer?: OfferReturn }): React.JSX.Element {
  const board = useGatedStyle('boardFinish', FREE_BOARD_FINISHES, DEFAULT_FINISH, 'board', offer);
  return (
    <>
      <BoardFinishPicker value={board.value} onChange={board.onChange} tag={board.tag} />
      {board.gate}
    </>
  );
}

type StyleKey = 'cardFront' | 'cardBack' | 'boardFinish';

/** Where each style's names live in i18n, under `settings.`. */
const STYLE_NAMES = {
  cardFront: 'cardFronts',
  cardBack: 'cardBacks',
  boardFinish: 'boardFinishes',
} as const satisfies Record<StyleKey, string>;

/**
 * A style picker's value, its tag and its gate, for a player who may or may
 * not be a Game Surface member (the `game-paywall` sketch).
 *
 * A member sees no tags and saves whatever they pick. Anyone else browses every
 * style, but only a free one is saved. A paid one is a preview held here, and
 * nothing reaches the store while it shows, so leaving the page drops it and
 * the saved style stays free. The gate names the style they keep: the one the
 * table draws (`store/membership.ts`), which for a lapsed member whose saved
 * style is paid is the default.
 */
function useGatedStyle<K extends StyleKey>(
  key: K,
  free: ReadonlySet<SettingsState[K]>,
  fallback: SettingsState[K],
  door: OfferDoor,
  offer: OfferReturn | undefined,
): {
  readonly value: SettingsState[K];
  readonly onChange: (value: SettingsState[K]) => void;
  readonly tag: ReactNode;
  readonly gate: ReactNode;
} {
  const { t } = useTranslation();
  const saved = useSettingsStore((state) => state[key]);
  const member = useEntitlement('surface');
  // Back from the offer, the page shows the style the player went to ask about.
  const [preview, setPreview] = useState<SettingsState[K] | null>(
    () => parkedPreview(door, offer) as SettingsState[K] | null,
  );
  const save = (value: SettingsState[K]): void => {
    useSettingsStore.getState().set(key, value);
  };
  if (member) return { value: saved, onChange: save, tag: null, gate: null };

  const shown = preview ?? saved;
  const locked = !free.has(shown);
  const keeping = free.has(saved) ? saved : fallback;
  return {
    value: shown,
    onChange: (value) => {
      if (free.has(value)) {
        setPreview(null);
        save(value);
      } else {
        setPreview(value);
      }
    },
    tag: <PaywallTag locked={locked} />,
    gate: locked && (
      <MemberGate
        door={door}
        keeping={t('settings.paywall.keeping', {
          name: t(`settings.${STYLE_NAMES[key]}.${keeping}`),
        })}
        preview={shown}
        offer={offer}
      />
    ),
  };
}

export function HighContrastSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useSettingsStore((state) => state.highContrastFelt);
  return (
    <Switch
      label={t('settings.highContrastFelt')}
      hint={t('settings.highContrastFeltHint')}
      checked={on}
      onChange={(value) => {
        useSettingsStore.getState().set('highContrastFelt', value);
      }}
    />
  );
}

export function ReducedMotionSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useSettingsStore((state) => state.reducedMotion);
  return (
    <Switch
      label={t('settings.reducedMotion')}
      hint={t('settings.reducedMotionHint')}
      checked={on}
      onChange={(value) => {
        useSettingsStore.getState().set('reducedMotion', value);
      }}
    />
  );
}

export function ConfirmPlaySwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useSettingsStore((state) => state.confirmPlay);
  return (
    <Switch
      label={t('settings.confirmPlay')}
      hint={t('settings.confirmPlayHint')}
      checked={on}
      onChange={(value) => {
        useSettingsStore.getState().set('confirmPlay', value);
      }}
    />
  );
}

export function AutoPlayForcedSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useSettingsStore((state) => state.autoPlayForced);
  return (
    <Switch
      label={t('settings.autoPlayForced')}
      hint={t('settings.autoPlayForcedHint')}
      checked={on}
      onChange={(value) => {
        useSettingsStore.getState().set('autoPlayForced', value);
      }}
    />
  );
}

export function BotSpeedChoice(): React.JSX.Element {
  const { t } = useTranslation();
  const speed = useSettingsStore((state) => state.botSpeed);
  return (
    <Choice
      label={t('settings.botSpeed')}
      hint={t('settings.botSpeedHint')}
      value={speed}
      options={BOT_SPEEDS.map((value) => ({
        value,
        label: t(`settings.botSpeed_${value}`),
      }))}
      onChange={(value) => {
        useSettingsStore.getState().set('botSpeed', value as BotSpeed);
      }}
    />
  );
}

/**
 * Which way your lines run on a Cross board: Rows, Columns or Flexible.
 *
 * Display only. It turns the drawing on this player's screen and changes
 * nothing about the game (`table/cross/transpose.ts`).
 */
export function CrossPositionChoice(): React.JSX.Element {
  const { t } = useTranslation();
  const position = useSettingsStore((state) => state.crossPosition);
  return (
    <Choice
      label={t('settings.crossPosition')}
      hint={t('settings.crossPositionHint')}
      value={position}
      options={CROSS_POSITIONS.map((value) => ({
        value,
        label: t(`settings.crossPosition_${value}`),
      }))}
      onChange={(value) => {
        useSettingsStore.getState().set('crossPosition', value as CrossPosition);
      }}
    />
  );
}

export function SoundSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useSettingsStore((state) => state.sound);
  return (
    <Switch
      label={t('settings.sound')}
      checked={on}
      onChange={(value) => {
        useSettingsStore.getState().set('sound', value);
      }}
    />
  );
}

/**
 * The slider stays mounted while muted so the mute reads as a mute: a control
 * that vanishes looks like a control you lost.
 */
export function VolumeLevel(): React.JSX.Element {
  const { t } = useTranslation();
  const volume = useSettingsStore((state) => state.volume);
  const sound = useSettingsStore((state) => state.sound);
  return (
    <Level
      label={t('settings.volume')}
      value={volume}
      disabled={!sound}
      onChange={(value) => {
        useSettingsStore.getState().set('volume', value);
      }}
    />
  );
}

export function HapticsSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const on = useSettingsStore((state) => state.haptics);
  return (
    <Switch
      label={t('settings.haptics')}
      hint={t('settings.hapticsHint')}
      checked={on}
      onChange={(value) => {
        useSettingsStore.getState().set('haptics', value);
      }}
    />
  );
}
